<template>
  <v-dialog 
    :value="value"
    width="600"
    @click:outside="cancel"
  >
    <v-card>
      <v-card-title>Copy Promotion</v-card-title>
      <v-card-text>
        <v-form v-model="valid" ref="form" lazy-validation>
          <v-container>
            <v-row>
              <v-col>
                <v-text-field
                  v-model="promoClone.promo_name"
                  label="Promotion Name"
                  hide-details="auto"
                  :rules="[v => !!v || 'Promotion Name is required', validateText]"
                  outlined
                  :disabled="$auth.tenant === 'alliance-retail-group'"
                  dense>
                </v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <v-text-field
                  v-model="promoClone.promo_number"
                  label="Promotion Number"
                  hide-details="auto"
                  :rules="[promoNumberValidation]"
                  outlined
                  dense>
                </v-text-field>
              </v-col>
              <v-col>
                <v-select
                  v-model="promoClone.promo_category"
                  label="Promotion Category"
                  :menu-props="{ offsetY: true }"
                  :items="validCloneTo"
                  :rules="[v => !!v || 'Promotion Category is required']"
                  item-text="name"
                  item-color="action"
                  hide-details="auto"
                  return-object
                  @change="validateDates"
                  outlined
                  dense>
                </v-select>
              </v-col>
            </v-row>
            <v-row>
              <v-col class="px-2 py-0">
                <PromoDates
                  ref="promoDates"
                  :promo="promoClone"
                  hide-details="auto"
                  :readOnlyPromo="false">
                </PromoDates>
              </v-col>
            </v-row>
            <v-row v-if="isToDSDInvoice || isToAdBillingMode">
              <v-col cols="6">
                <v-menu
                  v-model="ad_dt_picker"
                  transition="scale-transition"
                  offset-y
                  max-width="290px"
                  min-width="290px">
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="promoClone.ad_date"
                      label="Ad Date"
                      type="date"
                      prepend-inner-icon="mdi-calendar"
                      dense
                      ref="ad_dt"
                      background-color="#fff"
                      clearable
                      outlined
                      hide-details="auto"
                      :rules="[isValidAdDate]"
                      v-on="on"
                      v-bind="attrs">
                    </v-text-field>
                  </template>
                  <v-date-picker 
                    v-model="promoClone.ad_date"
                    color="action"
                    no-title>
                  </v-date-picker>
                </v-menu>
              </v-col>
            </v-row>
            <v-row v-if="isScanToComplex">
              <v-col>
                <v-text-field
                  v-model="promoClone.buy_x"
                  label="Buy X"
                  type="number"
                  min="1"
                  step="1"
                  hide-details="auto"
                  :rules="rules"
                  outlined
                  dense>
                </v-text-field>
              </v-col>
              <v-col>
                <v-text-field
                  v-model="promoClone.save_y"
                  label="Save $Y"
                  type="number"
                  min="0.01"
                  step=".01"
                  prefix="$"
                  hide-details="auto"
                  :rules="[...rules, isValidPrice]"
                  outlined
                  dense>
                </v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col v-if="promoClone.promo_category">
                <v-select
                  label="Promotion Type"
                  item-value="id"
                  item-text="name"
                  item-color="action"
                  :items="validCloneBillingModes"
                  v-model="promoClone.billing_mode"
                  background-color="#fff"
                  :rules="[v => !!v || 'Promotion Type is required']"
                  dense
                  hide-details="auto"
                  return-object
                  validate-on-blur
                  :menu-props="{ offsetY: true }"
                  outlined>
                </v-select>
              </v-col>
            <template v-if="showAllowanceFields">
              <v-col
                v-for="(field, i) in currentAllowanceFields"
                :key="i">
                <v-text-field
                  v-model="promoClone[`${field.value}`]"
                  :label="field.name"
                  type="number"
                  min="0.00"
                  step=".01"
                  prefix="$"
                  style="max-width: 254px"
                  hide-details="auto"
                  :rules="[isValidAllowancePrice]"
                  :error="showErrorState(field, promoClone)"
                  outlined
                  dense>
                </v-text-field>
              </v-col>
            </template>
            </v-row>
            <v-row v-if="!promo.promotion_price">
              <v-col>
                <v-text-field
                  v-model="promoClone.promotion_price"
                  label="Promotion Price"
                  type="number"
                  min="0.00"
                  step=".01"
                  prefix="$"
                  style="max-width: 254px"
                  hide-details="auto"
                  :rules="[isValidRequiredPrice]"
                  outlined
                  dense>
                </v-text-field>
              </v-col>
            </v-row>
             <v-row v-if="$auth.tenant === 'alliance-retail-group'">
              <v-col>
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <v-autocomplete
                      v-on="on"
                      v-model="promoClone.ad_group_parties"
                      label="Additional Ad Groups"
                      :items="availableAdGroups"
                      item-text="name"
                      item-value="id"
                      item-color="action"
                      :menu-props="{ offsetY: true }"
                      background-color="input"
                      hide-details="auto"
                      multiple
                      chips
                      small-chips
                      outlined
                      clearable
                      return-object
                      dense>
                    </v-autocomplete>
                  </template>
                  <span>Only ad groups associated with the item sources represented by the original promotion are available for selection.</span>
                </v-tooltip>
                <!-- NOTE: may be re-enabled in the future -->
                <!-- <CloneModalAdGroups
                  :value.sync="promoClone.ad_group_parties"
                  :promoItemSources="promoItemSources"
                  :validSources="validSources"
                  :existingAdGroups="promoStores">
                </CloneModalAdGroups> -->
              </v-col>
            </v-row>
          </v-container>
        </v-form>
      </v-card-text>
      <v-divider class="mt-8"></v-divider>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          @click="cancel"
          text
          color="grey darken-2">
          Cancel
        </v-btn>
        <v-btn
          :loading="loading"
          color="green darken-2"
          class="white--text"
          @click="clonePromo">
          Create
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
<script>
// API
import Promos from '@/axios/promotion-endpoint'
import PromoStore from '@/axios/promotion-store-endpoint'
// Mixins
import { displayAlert } from '@/mixins/alert'
import { formValidation } from '@/mixins/form-validation'
import { userAccess } from '@/mixins/user-access'
import { utils } from '@/mixins/utils'
// Components
import PromoDates from '@/components/PromoDates.vue'
// import CloneModalAdGroups from '@/components/CloneModalAdGroups.vue'
export default {
  data () {
    return {
      loading: false,
      valid: true,
      ad_dt_picker: false,
      promoClone: {
        promo_name: null,
        promo_number: null,
        promo_category: null,
        start_dt: null,
        end_dt: null,
        ad_date: null,
        buy_x: null,
        save_y: null,
        billback_allowance: null,
        amap_allowance: null,
        early_buy_allowance: null,
        rebate_amount: null,
        billing_mode: null,
        promotion_price: null
      },
      existingAdGroups: [],
      rules: [v => !!v || 'Required field'],
      scanToComplex: {
        cloneFrom: ['AMOUNT_OFF', 'PERCENT_OFF'],
        cloneTo: ['BUY_X_SAVE_$Y']
      },
      scanToBillback: {
        cloneFrom: ['AMOUNT_OFF'],
        cloneTo: ['BILLBACK']
      },
      billbackToScan: {
        cloneFrom: ['BILLBACK'],
        cloneTo: ['AMOUNT_OFF']
      },
      allowanceFields: [
        { name: 'Billback Allowance', value: 'billback_allowance' },
        { name: 'Scan Allowance', value: 'rebate_amount' }
      ],
      billbackError: false,
      scanError: false
    }
  },
  name: 'CloneModal',
  components: { PromoDates },
  mixins: [displayAlert, formValidation, userAccess, utils],
  props: {
    value: Boolean,
    promo: Object,
    promoStores: Array,
    promoItemSources: Array,
    validSources: Array,
    itemSourceRels: Array
  },
  watch: {
    'promoClone.promo_category': {
      handler (newValue, oldValue) {
        if ((oldValue || this.isAMAP) && newValue?.valid_billing_modes?.length > 0) {
          const validModes = this.validCloneBillingModes
          if (validModes.length === 1) {
            this.promoClone.billing_mode = validModes[0]
          } else {
            this.promoClone.billing_mode = null
          }
        }
        this.$refs.promoDates.validateForm()
      },
      deep: true
    },
    isScanToComplex: {
      handler (newValue) {
        if (newValue === false) {
          this.promoClone.buy_x = null
          this.promoClone.save_y = null
        }
      }
    },
    isScanToBillback: {
      handler (newValue) {
        if (newValue === false) {
          this.promoClone.billback_allowance = null
          this.resetForm()
        }
      }
    },
    isToAdBillingMode: {
      handler (newValue, oldValue) {
        if (newValue === false && oldValue === true) {
          this.promoClone.ad_date = null
        }
      }
    },
    isToDSDInvoice: {
      handler (newValue, oldValue) {
        if (newValue === false && oldValue === true) {
          this.promoClone.ad_date = null
        }
      }
    },
    isBillbackToScan: {
      handler (newValue) {
        const { billback_allowance } = this.promo
        if (newValue === false) {
          this.promoClone.billback_allowance = billback_allowance
          this.promoClone.rebate_amount = null
        } else {
          this.promoClone.billback_allowance = null
          this.promoClone.rebate_amount = null
        }
        this.resetForm()
      }
    }
  },
  async created () {
    if (this.promoCategories.length === 0) {
      await this.$store.dispatch('getPromoCategories')
    }
    this.initPromoFields()
  },
  computed: {
    promoCategories () {
      return this.sortByKey(this.userPromoCategories, 'name')
    },
    billing_modes () {
      return this.$store.getters.billing_modes.filter(mode => mode?.constant !== 'AMAP')
    },
    validCloneBillingModes () {
      const validIds = this.promoClone.promo_category?.valid_billing_modes || []
      if (validIds.length > 0) {
        return this.billing_modes.filter(mode => validIds.includes(mode?.id))
      }
      return this.billing_modes
    },
    currentAllowanceFields () {
      let fields = []
      if (this.cloneToType === 'BILLBACK') {
        fields = ['billback_allowance']
      } else if (this.cloneToType === 'AMOUNT_OFF') {
        fields = ['rebate_amount']
      }
      return this.allowanceFields.filter(field => {
        return fields.includes(field.value)
      })
    },
    showAllowanceFields () {
      return (this.cloneToCategory?.billback_promotion && !this.cloneToNonAllowance)
        || (this.isScanToBillback || this.isBillbackToScan)
        || (this.isNonAllowanceToAllowance)
    },
    cloneFromCategory () {
      return this.promo?.promo_category
    },
    cloneToCategory () {
      return this.promoClone.promo_category
    },
    cloneFromType () {
      return this.cloneFromCategory?.promo_type?.constant
    },
    cloneToType () {
      return this.cloneToCategory?.promo_type?.constant
    },
    validTypes () {
      if (this.promo) {
        const validCrossTypes = this.getValidCrossCategoryTypes(this.cloneFromType)
        return [this.cloneFromType, ...validCrossTypes]
      }
      return []
    },
    validCloneTo () {
      let valid = this.promoCategories.filter(cat => this.validTypes.includes(cat.promo_type.constant)) 
      if (this.isAMAP){
        return valid
      }
      const specialFourWeekIndex = valid.findIndex(cat => cat.name === 'Special 4 Week')
      if (specialFourWeekIndex > -1) valid.push(valid.splice(specialFourWeekIndex, 1)[0])
      return valid.filter(cat => cat.name != "AMAP")
    },
    isCrossCategory () {
      if (this.promo && this.cloneToCategory?.id) {
        return this.cloneFromCategory.id !== this.cloneToCategory.id
      }
      return false
    },
    cloneToNonAllowance () {
      return this.isNonAllowanceCategory(this.cloneToCategory?.name)
    },
    isNonAllowanceToAllowance () {
      return this.isNonAllowanceCategory(this.cloneFromCategory?.name) && !this.cloneToNonAllowance
    },
    isToAdBillingMode() {
      return this.$auth.tenant === 'alliance-retail-group' && (this.promoClone?.promo_category?.name.includes("Ad") || this.isScanToComplex)
    },
    isToDSDInvoice() {
      return this.$auth.tenant === 'alliance-retail-group' && (this.promoClone?.promo_category?.name.includes("DSD Off Invoice"))
    },
    isScanToComplex () {
      return (this.scanToComplex.cloneFrom.includes(this.cloneFromType))
        && (this.scanToComplex.cloneTo.includes(this.cloneToType))
    },
    isScanToBillback () {
      return !this.cloneToNonAllowance
        && (this.scanToBillback.cloneFrom.includes(this.cloneFromType))
        && (this.scanToBillback.cloneTo.includes(this.cloneToType))
    },
    isBillbackToScan () {
      return !this.cloneToNonAllowance
        && (this.billbackToScan.cloneFrom.includes(this.cloneFromType))
        && (this.billbackToScan.cloneTo.includes(this.cloneToType))
    },
    isAMAPtoNonAMAP () {
      return (this.isAMAP && this.cloneToCategory.name !== 'AMAP')
    },
    scanAllowanceRequired () {
      return this.isBillbackToScan || (this.isNonAllowanceToAllowance && this.cloneToCategory.scan_promotion)
    },
    billbackAllowanceRequired () {
      return this.isScanToBillback
        || (this.isNonAllowanceToAllowance && this.cloneToCategory.billback_promotion)
        || (this.isAMAPtoNonAMAP && this.cloneToCategory.billback_promotion)
    },
    validScanClone () {
      return this.scanAllowanceRequired ? this.hasScanAllowance(this.promoClone) : true
    },
    validBillbackClone () {
      return this.billbackAllowanceRequired ? this.hasBillingAllowance(this.promoClone) : true
    },
    groupPartyTypes () {
      let types = ['REGION_GROUP', 'STORE_GROUP']
      if (this.tenant === 'alliance-retail-group') {
        types.push('DISTRIBUTION_CENTER')
        if (!this.limitAccessByRelatedVendors) {
          types.push('SUBGROUP')
        }
      }
      return types
    },
    activeAdGroups () {
      if (this.$auth.tenant !== 'alliance-retail-group') return []
      let groups = this.$store.getters.adGroups.filter((g) => {
      return g?.attributes?.AD_GROUP_IS_ACTIVE === 'True'
        && !this.totalPromoExcludedAdGroups.some(ag => ag.id === g.id)
      })
      if (this.limitAccessByRelatedStores) {
        groups = groups.filter(g => this.userRelatedPartyIds.includes(g.id))
      }
      return groups
    },
    availableAdGroups () {
      if (this.$auth.tenant !== 'alliance-retail-group') return []
      let groups = this.activeAdGroups
      if (this.promoStores.length > 0) {
        groups = this.activeAdGroups.filter(ag => {
          return !this.promoStores.some(s => s.store_id === ag.id)
            && this.promoItemDCs.some(dc => dc.member_ids.includes(ag.id))
        })
      }
      return this.sortByKey(groups, 'name')
    },
    promoItemDCs() {
      if (this.$auth.tenant !== 'alliance-retail-group') return []
      if (this.promoItemSources.length > 0) {
        return this.promoItemSources.map(source => {
          const rel = this.itemSourceRels.find(rel => rel.source_id === source)
          return this.activeDCs.find(dc => dc?.id === rel.party_id)
        })
      }
      return []
    },
    activeDCs () {
      return this.$store.getters.activeDistributionCenters
    },
    totalPromoExcludedAdGroups() {
      return this.$store.getters.totalPromoExcludedAdGroups
    },
    adPromoOnlyExcludedAdGroups() {
      return this.$store.getters.adPromoOnlyExcludedAdGroups
    },
    excludedAdGroups() {
      return (this.promoClone.billing_mode.constant === 'AD' && this.promoClone.promo_category.name != 'DSD Off Invoice') 
        ? [...this.adPromoOnlyExcludedAdGroups, ...this.totalPromoExcludedAdGroups]
        : this.totalPromoExcludedAdGroups
    }
  },
  methods: {
    initPromoFields () {
      const {
        name, start_dt, end_dt, promo_category,
        billback_allowance, billing_mode, ad_date
      } = this.promo

      const defaultCat = this.validCloneTo.find(c => c.id === promo_category.id)
      this.promoClone.promo_name = name
      this.promoClone.start_dt = start_dt
      this.promoClone.end_dt = end_dt
      this.promoClone.ad_date = ad_date
      this.promoClone.promo_category = defaultCat
      this.promoClone.billing_mode = billing_mode

      if (promo_category.billback_promotion) {
        this.promoClone.billback_allowance = billback_allowance
      }
      this.initPromoNumber()
    },
    isValidAdDate (value) {
      if (this.isToAdBillingMode) {
        if (this.$auth.tenant === 'alliance-retail-group' && this.promoClone.ad_date && this.promoClone.start_dt !== this.promoClone.ad_date) {
          return "Ad Date must be the same as Start Date"
        }
        return !!value || 'Ad Date is required'
      }
      return true
    },
    async initPromoNumber() {
      try {
        const res = await Promos.suggestedPromoNumber()
        this.promoClone.promo_number = res?.data?.promo_number
      } catch (err) {
        this.handleError(err)
      }
    },
    async clonePromo () {
      const valid = this.validate()
      if (!valid) return
      this.loading = true
      this.promoClone = this.$config.nullifyEmptyStrings(this.promoClone)
      const payload = { 
        ...this.promoClone,
        billing_mode: this.promoClone.billing_mode.id,
        category: this.promoClone.promo_category.id,
        promo_start: this.promoClone.start_dt,
        promo_end: this.promoClone.end_dt,
        ad_date: this.promoClone.ad_date,
        variance: this.scanAllowanceRequired ? this.tenantDefaultVariance[this.tenant] : this.promo.variance
      }
      try {
        const res = await Promos.clone(this.promo.id, payload)
        if (res.data?.id) {
          if (this.promoClone.ad_group_parties) await this.addAdGroups(res.data?.id)
          this.$emit('success', res.data.id, payload.promo_name) 
          this.resetForm()
        }
      } catch (err) {
        this.handleError(err)
      } finally {
        this.loading = false
      }
    },
    async addAdGroups (clonedPromoId) {
      this.loading = true
      try {
        const existingPartyIds = this.promoStores.map(ps => ps.id)
        const promises = []
        this.promoClone.ad_group_parties.forEach(party => {
          const exists = existingPartyIds.includes(party.id)
          if (!exists && this.groupPartyTypes.includes(party?.party_type.constant)) {
            return this.addGroupMembers(clonedPromoId, party)
          } else if (!exists) {
            const payload  = {
              promo_id: clonedPromoId,
              store_id: party.id,
              include_store: true,
              enforce_compliance: true
            }
            promises.push(PromoStore.post(payload))
          }
        })
        const { rejected } = await this.getAllSettled(promises, true)
        if (rejected.length > 0) throw rejected
      } catch (err) {
        this.handleError(err)
        this.loading = false
      }
    },
    async addGroupMembers (clonedPromoId, party) {
      const memberPartyIds = await this.getMemberPartyIds(party, this.memberPartyTypes)
      const membersToAdd = this.getAdGroupsToAdd(memberPartyIds)
      const promises = membersToAdd.map(partyId => {
        const payload  = {
          promo_id: clonedPromoId,
          store_id: partyId,
          include_store: true,
          enforce_compliance: true
        }
        return PromoStore.post(payload)
      })
      try {
        const { rejected } = await this.getAllSettled(promises, true)
        if (rejected.length > 0) throw rejected
      } catch (err) {
        this.handleError(err)
      }
    },
    getAdGroupsToAdd(groupList) {
      return groupList.filter(id => {
        const isNewStore = id && !this.promoStores.some(store => store?.store_id === id)
        return this.tenant === 'alliance-retail-group' 
          ? isNewStore && !this.excludedAdGroups.some(ad => ad.id === id) 
          : isNewStore
      })
    },
    cancel () {
      this.$emit('cancel')
      this.resetForm()
    },
    validate () {
      const validFields = this.$refs.form.validate()
      const validDateFields = this.$refs.promoDates.validate()
      if (!this.validBillbackClone) {
        this.billbackError = true
        const err = 'Please enter a billback allowance value.'
        this.emitAlert(true, 'warning', err)
        return false
      }
      if (!this.validScanClone) {
        this.scanError = true
        this.emitAlert(true, 'warning', 'Please enter a scan allowance value.')
        return false
      }
      return (validFields && validDateFields)
    },
    resetForm () {
      this.$refs.form.resetValidation()
      this.billbackError = false
      this.scanError = false
      this.resetAlert()
    },
    validateDates () {
      this.$refs.promoDates.validateForm()
    },
    promoNumberValidation() {
      const clone_number = this.promoClone.promo_number
      const promo_number = this.promo.promo_number
      if (clone_number && promo_number) {
        if (clone_number === promo_number)
          return 'Promotion number cannot be the same as parent'
      }
      return true
    },
    getValidCrossCategoryTypes (cloneFromType) {
      return [this.scanToComplex, this.scanToBillback, this.billbackToScan].flatMap(config => {
        if (config.cloneFrom.includes(cloneFromType)) {
          return config.cloneTo
        }
        return []
      })
    }
  }
}
</script>